import React, { useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon, ServerIcon, CpuChipIcon } from '@heroicons/react/24/outline';

interface EKSCluster {
  clusterName: string;
  region: string;
  version: string;
  status: string;
  nodeGroups?: NodeGroup[];
  selfManagedNodes?: EC2Node[];
  totalNodes?: number;
}

interface NodeGroup {
  nodeGroupName: string;
  instanceTypes: string[];
  scalingConfig: {
    minSize: number;
    maxSize: number;
    desiredSize: number;
  };
  labels?: Record<string, string>;
  status?: string;
}

interface EC2Node {
  instanceId: string;
  instanceType: string;
  state: string;
  vcpus?: number;
  memory?: number;
  region: string;
}

interface EKSDetailsViewProps {
  eksData: any[];
  ec2Instances?: any[];
}

const EKSDetailsView: React.FC<EKSDetailsViewProps> = ({ eksData, ec2Instances = [] }) => {
  const [expandedClusters, setExpandedClusters] = useState<Set<string>>(new Set());
  const [expandedNodeGroups, setExpandedNodeGroups] = useState<Set<string>>(new Set());

  const toggleCluster = (clusterName: string) => {
    const newExpanded = new Set(expandedClusters);
    if (newExpanded.has(clusterName)) {
      newExpanded.delete(clusterName);
    } else {
      newExpanded.add(clusterName);
    }
    setExpandedClusters(newExpanded);
  };

  const toggleNodeGroup = (key: string) => {
    const newExpanded = new Set(expandedNodeGroups);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedNodeGroups(newExpanded);
  };

  // Find EC2 instances that belong to an EKS cluster
  const getClusterEC2Instances = (clusterName: string, region: string) => {
    return ec2Instances.filter(instance => {
      const clusterTag = instance.tags?.['aws:eks:cluster-name'];
      return clusterTag === clusterName && instance.region === region && instance.state === 'running';
    });
  };

  // Calculate total resources for a cluster
  const calculateClusterTotals = (cluster: any, selfManagedInstances: any[]) => {
    let totalVcpus = 0;
    let totalMemory = 0;
    let totalStorage = 0;
    let monthlyCost = 0;
    
    // Add self-managed nodes
    selfManagedInstances.forEach(instance => {
      if (instance.state === 'running') {
        totalVcpus += instance.vcpus || 0;
        totalMemory += instance.memory || 0;
        // Assume 100GB EBS per instance for storage estimate
        totalStorage += 100;
        // Estimate cost at $0.05 per vCPU hour
        monthlyCost += (instance.vcpus || 0) * 0.05 * 720;
      }
    });
    
    // Add managed node groups
    if (cluster.nodeGroups) {
      cluster.nodeGroups.forEach((nodeGroup: any) => {
        const instanceType = nodeGroup.instanceTypes?.[0];
        if (instanceType) {
          const specs = getInstanceSpecs(instanceType);
          const nodeCount = nodeGroup.scalingConfig?.desiredSize || 0;
          totalVcpus += specs.vcpus * nodeCount;
          totalMemory += specs.memory * nodeCount;
          // Assume 100GB EBS per node for storage estimate
          totalStorage += 100 * nodeCount;
          // Estimate cost at $0.05 per vCPU hour
          monthlyCost += specs.vcpus * nodeCount * 0.05 * 720;
        }
      });
    }
    
    return {
      totalVcpus: Math.round(totalVcpus),
      totalMemory: Math.round(totalMemory),
      totalStorage: Math.round(totalStorage),
      monthlyCost: Math.round(monthlyCost)
    };
  };

  // Get instance type specs - try to get from actual EC2 data first
  const getInstanceSpecs = (instanceType: string) => {
    // Try to find specs from actual EC2 instances
    const instanceWithType = ec2Instances?.find(i => i.instanceType === instanceType);
    if (instanceWithType && instanceWithType.vcpus && instanceWithType.memory) {
      return { vcpus: instanceWithType.vcpus, memory: instanceWithType.memory };
    }
    
    // Fallback to common instance types
    const specs: Record<string, { vcpus: number; memory: number }> = {
      't3.micro': { vcpus: 2, memory: 1 },
      't3.small': { vcpus: 2, memory: 2 },
      't3.medium': { vcpus: 2, memory: 4 },
      't3.large': { vcpus: 2, memory: 8 },
      't3.xlarge': { vcpus: 4, memory: 16 },
      't3.2xlarge': { vcpus: 8, memory: 32 },
      'c5.large': { vcpus: 2, memory: 4 },
      'c5.xlarge': { vcpus: 4, memory: 8 },
      'c5.2xlarge': { vcpus: 8, memory: 16 },
      'c5.4xlarge': { vcpus: 16, memory: 32 },
      'c6g.medium': { vcpus: 1, memory: 2 },
      'c6g.large': { vcpus: 2, memory: 4 },
      'c6g.xlarge': { vcpus: 4, memory: 8 },
      'c6g.2xlarge': { vcpus: 8, memory: 16 },
      'm5.large': { vcpus: 2, memory: 8 },
      'm5.xlarge': { vcpus: 4, memory: 16 },
      'm5.2xlarge': { vcpus: 8, memory: 32 },
      'm5.4xlarge': { vcpus: 16, memory: 64 },
      'm6i.large': { vcpus: 2, memory: 8 },
      'm6i.xlarge': { vcpus: 4, memory: 16 },
      'm6i.2xlarge': { vcpus: 8, memory: 32 },
    };
    return specs[instanceType] || { vcpus: 0, memory: 0 };
  };

  const renderNodeGroupNodes = (nodeGroup: NodeGroup, clusterName: string) => {
    const instanceType = nodeGroup.instanceTypes?.[0] || 'Unknown';
    const specs = getInstanceSpecs(instanceType);
    const nodeCount = nodeGroup.scalingConfig?.desiredSize || 0;
    const totalVcpus = specs.vcpus * nodeCount;
    const totalMemory = specs.memory * nodeCount;

    return (
      <div className="ml-12 text-sm text-gray-600 dark:text-gray-400">
        {nodeCount > 0 && (
          <div className="flex items-center space-x-4 py-1">
            <span className="font-mono">{instanceType}</span>
            <span>{nodeCount} node{nodeCount !== 1 ? 's' : ''}</span>
            <span className="text-gray-500">•</span>
            <span>{totalVcpus} vCPUs total</span>
            <span className="text-gray-500">•</span>
            <span>{totalMemory} GB RAM total</span>
            {nodeGroup.status && (
              <>
                <span className="text-gray-500">•</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  nodeGroup.status === 'ACTIVE' 
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                    : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                }`}>
                  {nodeGroup.status}
                </span>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderSelfManagedNodes = (instances: any[], clusterName: string) => {
    // Group instances by type
    const instancesByType = instances.reduce((acc, instance) => {
      const type = instance.instanceType;
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(instance);
      return acc;
    }, {} as Record<string, any[]>);

    return (
      <div className="ml-8">
        {Object.entries(instancesByType).map(([instanceType, typeInstances]) => {
          const typedInstances = typeInstances as any[];
          const specs = getInstanceSpecs(instanceType);
          const runningCount = typedInstances.filter((i: any) => i.state === 'running').length;
          const totalVcpus = (typedInstances[0]?.vcpus || specs.vcpus) * runningCount;
          const totalMemory = (typedInstances[0]?.memory || specs.memory) * runningCount;

          return (
            <div key={instanceType} className="ml-4 text-sm text-gray-600 dark:text-gray-400 py-1">
              <div className="flex items-center space-x-4">
                <span className="font-mono">{instanceType}</span>
                <span>{runningCount} instance{runningCount !== 1 ? 's' : ''}</span>
                <span className="text-gray-500">•</span>
                <span>{totalVcpus} vCPUs total</span>
                <span className="text-gray-500">•</span>
                <span>{totalMemory} GB RAM total</span>
                <span className="text-gray-500">•</span>
                <span className="text-xs text-gray-500">Self-managed</span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (!eksData || eksData.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        No EKS clusters found
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {eksData.map((cluster) => {
        const isExpanded = expandedClusters.has(cluster.clusterName);
        const selfManagedInstances = getClusterEC2Instances(cluster.clusterName, cluster.region);
        const hasSelfManagedNodes = selfManagedInstances.length > 0;
        const hasContent = (cluster.nodeGroups && cluster.nodeGroups.length > 0) || hasSelfManagedNodes;
        const clusterTotals = calculateClusterTotals(cluster, selfManagedInstances);

        return (
          <div key={cluster.clusterName} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            {/* Cluster Header */}
            <div 
              className={`px-4 py-3 ${hasContent ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50' : ''}`}
              onClick={() => hasContent && toggleCluster(cluster.clusterName)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {hasContent && (
                    <div className="text-gray-400">
                      {isExpanded ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
                    </div>
                  )}
                  <ServerIcon className="h-5 w-5 text-indigo-500" />
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">{cluster.clusterName}</span>
                    <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                      ({cluster.region}, v{cluster.version})
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4 lg:space-x-6">
                  {/* Resource Totals */}
                  <div className="hidden sm:flex items-center space-x-3 lg:space-x-4 text-sm">
                    <div className="text-center px-2">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">vCPUs</div>
                      <div className="font-semibold text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-700 rounded px-2 py-1">
                        {clusterTotals.totalVcpus}
                      </div>
                    </div>
                    <div className="text-center px-2">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Memory</div>
                      <div className="font-semibold text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-700 rounded px-2 py-1">
                        {clusterTotals.totalMemory} GB
                      </div>
                    </div>
                    <div className="text-center px-2">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Storage</div>
                      <div className="font-semibold text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-700 rounded px-2 py-1">
                        {clusterTotals.totalStorage} GB
                      </div>
                    </div>
                    <div className="text-center px-2">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Est. Cost</div>
                      <div className="font-semibold text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30 rounded px-2 py-1">
                        ${clusterTotals.monthlyCost}/mo
                      </div>
                    </div>
                  </div>
                  
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    cluster.status === 'ACTIVE' 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                  }`}>
                    {cluster.status}
                  </span>
                </div>
              </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && hasContent && (
              <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-2">
                {/* Self-Managed Nodes */}
                {hasSelfManagedNodes && (
                  <div className="py-2">
                    <div className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300 ml-8">
                      <CpuChipIcon className="h-4 w-4 text-gray-400" />
                      <span>Self-managed nodes:</span>
                    </div>
                    {renderSelfManagedNodes(selfManagedInstances, cluster.clusterName)}
                  </div>
                )}

                {/* Node Groups */}
                {cluster.nodeGroups && cluster.nodeGroups.map((nodeGroup: any) => {
                  const nodeGroupKey = `${cluster.clusterName}-${nodeGroup.nodeGroupName}`;
                  const isNodeGroupExpanded = expandedNodeGroups.has(nodeGroupKey);

                  return (
                    <div key={nodeGroup.nodeGroupName} className="py-2">
                      <div 
                        className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30 rounded px-2 py-1 ml-6"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleNodeGroup(nodeGroupKey);
                        }}
                      >
                        <div className="text-gray-400">
                          {isNodeGroupExpanded ? <ChevronDownIcon className="h-3 w-3" /> : <ChevronRightIcon className="h-3 w-3" />}
                        </div>
                        <CpuChipIcon className="h-4 w-4 text-blue-500" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {nodeGroup.nodeGroupName}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          ({nodeGroup.scalingConfig?.desiredSize || 0} nodes)
                        </span>
                      </div>
                      {isNodeGroupExpanded && renderNodeGroupNodes(nodeGroup, cluster.clusterName)}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default EKSDetailsView;